import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';

import Spacer from 'design-system/components/Spacer';
import Button, { ButtonType } from 'inquiry/components/Button';
import Dialog from 'inquiry/components/Dialog';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useStepSelfieStyleContext } from 'inquiry/context/StepSelfieStyleContext';
import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import { ConfigurablePosesScanner } from 'inquiry/scanners/ConfigurablePosesScanner';
import InquirySelfiePendingView from 'inquiry/views/InquirySelfiePendingView';
import useIsMounted from 'lib/hooks/useIsMounted';
import useToggle from 'lib/hooks/useToggle';
import { StepCombinedBaseConfig, StepSelfieConfig } from 'lib/interfaces/StepsConfig';
import { SelfieMatch } from 'lib/interfaces/verificationConfig';
import { SelfieVideoCaptureMethod } from 'verify/lib/interfaces/CaptureOption';
import {
  CapturedSelfie,
  CapturedSelfiesSubmitData,
  SelfieStep,
  SelfieThreePointPoseOrder,
} from 'verify/lib/interfaces/selfie';
import { getCurrentSelfieStep, getSelfiePoseOrder } from 'verify/lib/selfie';
import { CaptureMethod } from 'verify/models/verification';
import SelfieScanner from 'verify/scanners/SelfieScanner';
import { LOADING_BAR_ANIMATION_DURATION_MS } from 'verify/scanners/SelfieScanner/LoadingCircle';
import SelfieScannerEditorPreview from 'verify/scanners/SelfieScanner/SelfieScannerEditorPreview';

interface InquirySelfieRecordViewProps {
  audioEnabled: boolean;
  assets: StepSelfieConfig['assets']['record-page'];
  capturedSelfies: CapturedSelfie[];
  detectTimeout: number;
  enableVideoRecording?: boolean;
  featureFlags: FeatureFlagsMap;
  isEditorPreview?: boolean;
  localizations: StepSelfieConfig['localizations']['capture-page'];
  mobileExperienceLocalizations: StepSelfieConfig['localizations']['pending-page'];
  mobileExperienceVerticalPosition: StepCombinedBaseConfig['pending-page-text-vertical-position'];
  enableMobilePendingExperienceForWeb: boolean;
  orderedPoses?: SelfieThreePointPoseOrder;
  poseConfigs?: StepSelfieConfig['pose-configs'];
  requireHigherAccuracyCapture: boolean;
  requireSelfieMatch: SelfieMatch;
  uploadProgress?: number;
  videoCaptureMethods?: SelfieVideoCaptureMethod[];
  videoConstraints: MediaTrackConstraints;
  videoSessionJwt?: string | null;
  webCameraManualCaptureDelayMs: number | null;
  clearCapturedSelfies: () => void;
  handleComplete: (capturedSelfiesSubmitData: CapturedSelfiesSubmitData) => void;
  handleFailed: (error: Error | string, state: 'load' | 'capture') => void;
  handleSlowFaceDetection: () => void;
  handleRestartStep?: (handleRestartStepArgs?: { timeExceeded?: boolean }) => void;
  saveCapturedSelfie: (
    selfieStep: SelfieStep,
    imageDataUrl: string,
    captureMethod: CaptureMethod,
  ) => void;
}

export default function InquirySelfieRecordView(props: InquirySelfieRecordViewProps) {
  const [
    showSlowDetectionDialog,
    { enable: openSlowDetectionDialog, disable: closeSlowDetectionDialog },
  ] = useToggle();

  const selfieStyles = useStepSelfieStyleContext();
  const { modal: modalStyles, titleStyle, textStyle } = selfieStyles?.base ?? {};
  const buttonStyles = selfieStyles?.buttons;

  const poseOrder = getSelfiePoseOrder({
    orderedPoses: props.orderedPoses,
    requireSelfieMatch: props.requireSelfieMatch,
  });
  const isComplete = getCurrentSelfieStep(props.capturedSelfies, poseOrder) == null;

  const { t } = useTranslation();

  const [showPendingPage, setShowPendingPage] = useState(false);
  const isMounted = useIsMounted();

  useEffect(() => {
    /**
     * We show the pending page if the config is on and after the checkmark
     * animation finished playing. Because any custom loading asset is shown on
     * the pending page, we always use the default checkmark asset here.
     */
    if (isComplete && props.enableMobilePendingExperienceForWeb) {
      const timeout = setTimeout(() => {
        if (isMounted()) setShowPendingPage(true);
      }, LOADING_BAR_ANIMATION_DURATION_MS);

      return () => clearTimeout(timeout);
    }
  }, [isComplete]);

  // NOTE: [PER-27626] temporary workaround for One theming until routing updates for Selfie step
  if (showPendingPage) {
    return (
      <InquirySelfiePendingView
        assets={props.assets}
        localizations={props.mobileExperienceLocalizations}
        textVerticalPosition={props.mobileExperienceVerticalPosition}
      />
    );
  }

  return (
    <>
      <Dialog
        isOpen={showSlowDetectionDialog}
        onClose={closeSlowDetectionDialog}
        styles={modalStyles}
        data-test='inquiry-selfie-record-view__slow-detection-dialog'
        aria-labelledby='inquiry-selfie-record-view__slow-detection-dialog--title'
        aria-describedby='inquiry-selfie-record-view__slow-detection-dialog--description'
      >
        <ThemedTitle
          id='inquiry-selfie-record-view__slow-detection-dialog--title'
          styles={titleStyle}
        >
          <Markdown>{t('selfie.record.slow-detection-title')}</Markdown>
        </ThemedTitle>
        <ThemedParagraph
          id='inquiry-selfie-record-view__slow-detection-dialog--description'
          styles={textStyle}
        >
          <Markdown>{t('selfie.record.slow-detection-prompt')}</Markdown>
        </ThemedParagraph>

        <Spacer height={2} />

        <Button
          id='inquiry-selfie-record-view__slow-detection-dialog--wait-btn'
          data-test='inquiry-selfie-record-view__slow-detection-dialog--wait-btn'
          onClick={closeSlowDetectionDialog}
          styles={buttonStyles?.[ButtonType.Primary]}
        >
          {t('selfie.record.slow-detection-wait')}
        </Button>

        {/* Assume spacing is specified by styles if present */}
        {buttonStyles == null && <Spacer height={1} />}

        <Button
          id='inquiry-selfie-record-view__slow-detection-dialog--try-another-device-btn'
          data-test='inquiry-selfie-record-view__slow-detection-dialog--try-another-device-btn'
          onClick={props.handleSlowFaceDetection}
          buttonType={ButtonType.Secondary}
          styles={buttonStyles?.[ButtonType.Secondary]}
        >
          {t('selfie.record.slow-detection-try-another-device')}
        </Button>
      </Dialog>

      {props.localizations['title'] ? (
        <>
          <ThemedTitle styles={titleStyle}>{props.localizations['title']}</ThemedTitle>
          <Spacer height={2} />
        </>
      ) : null}

      {props.isEditorPreview ? (
        <SelfieScannerEditorPreview
          assets={props.assets}
          localizations={props.localizations}
          requireSelfieMatch={props.requireSelfieMatch}
          styles={selfieStyles}
          buttonStyles={buttonStyles}
          featureFlags={props.featureFlags}
        />
      ) : (
        <>
          {props.requireSelfieMatch === 'configurable_poses' ? (
            <ConfigurablePosesScanner
              assets={props.assets}
              delayUntilManualCaptureMs={props.webCameraManualCaptureDelayMs}
              detectTimeout={props.detectTimeout}
              enableVideoRecording={props.enableVideoRecording}
              localizations={props.localizations}
              poseOrder={poseOrder}
              requireHigherAccuracyCapture={props.requireHigherAccuracyCapture}
              styles={selfieStyles}
              handleComplete={props.handleComplete}
              handleFailed={props.handleFailed}
              handleRestartStep={props.handleRestartStep}
              handleSlowFaceDetection={props.handleSlowFaceDetection}
              videoConstraints={props.videoConstraints}
              videoCaptureMethods={props.videoCaptureMethods}
              videoSessionJwt={props.videoSessionJwt}
              enableMobilePendingExperienceForWeb={props.enableMobilePendingExperienceForWeb}
            />
          ) : (
            <SelfieScanner
              audioEnabled={props.audioEnabled}
              assets={props.assets}
              localizations={props.localizations}
              capturedSelfies={props.capturedSelfies}
              saveCapturedSelfie={props.saveCapturedSelfie}
              clearCapturedSelfies={props.clearCapturedSelfies}
              delayUntilManualCaptureMs={props.webCameraManualCaptureDelayMs}
              handleComplete={props.handleComplete}
              handleFailed={props.handleFailed}
              handleSlowFaceDetection={openSlowDetectionDialog}
              handleRestartStep={props.handleRestartStep}
              detectTimeout={props.detectTimeout}
              poseOrder={poseOrder}
              poseConfigs={props.poseConfigs}
              enableVideoRecording={props.enableVideoRecording}
              videoConstraints={props.videoConstraints}
              requireHigherAccuracyCapture={props.requireHigherAccuracyCapture}
              featureFlags={props.featureFlags}
              styles={selfieStyles}
              buttonStyles={buttonStyles}
              uploadProgress={props.uploadProgress}
              videoSessionJwt={props.videoSessionJwt}
              videoCaptureMethods={props.videoCaptureMethods}
              enableMobilePendingExperienceForWeb={props.enableMobilePendingExperienceForWeb}
            />
          )}
        </>
      )}
    </>
  );
}
